import { ComputedReferenceNode } from "@/components/expression/ComputedReferenceNode";
import { ComputedResultNode } from "@/components/expression/ComputedResultNode";
import { ConstantNode } from "@/components/expression/ConstantNode";
import { DecoratorNode } from "@/components/expression/DecoratorNode";
import { FunctionNode } from "@/components/expression/FunctionNode";
import { GroupNode } from "@/components/expression/GroupNode";
import { InlineNode } from "@/components/expression/InlineNode";
import { LabelNode } from "@/components/expression/LabelNode";
import { MatrixNode } from "@/components/expression/MatrixNode";
import { NumberNode } from "@/components/expression/NumberNode";
import { OperatorNode } from "@/components/expression/OperatorNode";
import { OperatorSymbolNode } from "@/components/expression/OperatorSymbolNode";
import { PlaceholderNode } from "@/components/expression/PlaceholderNode";
import { PlainTextNode } from "@/components/expression/PlainTextNode";
import { SymbolNode } from "@/components/expression/SymbolNode";
import { UnitNode } from "@/components/expression/UnitNode";
import { VariableNode } from "@/components/expression/VariableNode";

import type { ExpressionComponent, NodeRenderProps } from "./types";
import type { NodeModel } from "@/models/nodes/types";

type ExpressionNodeProps<T extends NodeModel["type"]> = {
  node: NodeModel<T>;
  nodeRenderProps?: NodeRenderProps;
  className?: string;
};

type ExpressionComponents = { [K in NodeModel["type"]]: ExpressionComponent<K> | undefined };

// Lazily initialized to avoid capturing undefined values from circular imports
// (ExpressionNode ↔ InlineNode ↔ Row ↔ ExpressionNode).
let RENDERER_MAP: ExpressionComponents | undefined;

function getRendererMap(): ExpressionComponents {
  if (!RENDERER_MAP) {
    RENDERER_MAP = {
      number: NumberNode,
      operator: OperatorNode,
      operatorSymbol: OperatorSymbolNode,
      variable: VariableNode,
      group: GroupNode,
      label: LabelNode,
      symbol: SymbolNode,
      constant: ConstantNode,
      function: FunctionNode,
      inline: InlineNode,
      wrapper: undefined,
      plainText: PlainTextNode,
      matrix: MatrixNode,
      decorator: DecoratorNode,
      unit: UnitNode,
      text: undefined,
      graph: undefined,
      graphExpression: undefined,
      placeholder: PlaceholderNode,
      computedReference: ComputedReferenceNode,
      computedResult: ComputedResultNode,
    };
  }

  return RENDERER_MAP;
}

export function ExpressionNode<T extends NodeModel["type"]>({
  node,
  nodeRenderProps,
  className,
}: ExpressionNodeProps<T>) {
  const Component = getRendererMap()[node.type] as ExpressionComponent<T> | undefined;
  if (!Component) {
    console.warn(`Unknown node type ${node.type}`, node);
    return null;
  }

  return <Component node={node} className={className} nodeRenderProps={nodeRenderProps} />;
}
